import React from 'react';
import { arrowDown } from '../../assets';

interface ISaleDetailItem {
  label: string;
  value: string;
}

interface ISaleTableData {
  customer: string;
  saleId: string;
  saleType: string;
  status: string;
  saleDate: string;
  saleFees: string;
  saleAmount: string;
  detailItems: ISaleDetailItem[];
}

interface SalesTableProps {
  salesHeaders: string[];
  saleTableData: ISaleTableData[];
  onDetailClick: (index: number) => void;
  toggleDetailIndex: number;
  toogleDetail: boolean;
}

interface ISaleDetailItem {
  label: string;
  value: string;
}

interface DetailRowProps {
  detailItems: ISaleDetailItem[];
}

export const DetailRow: React.FC<DetailRowProps> = ({ detailItems }) => {
  return (
    <section className="flex flex-wrap items-start pl-6 sm:pl-12 text-sm border-b border-slate-200">
      <div className="flex flex-col flex-1 w-full">
        <div className="border-t border-slate-200" />
        <div className="flex flex-col px-2 sm:px-4 w-full">
          {detailItems.map((item, index) => (
            <div key={index} className="flex flex-wrap gap-2 sm:gap-4 py-2 w-full">
              <div className="text-neutral-900 min-w-[120px] sm:w-[140px]">{item.label}:</div>
              <div className="text-neutral-900">{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const SalesTable: React.FC<SalesTableProps> = ({
  salesHeaders,
  saleTableData,
  onDetailClick,
  toggleDetailIndex,
  toogleDetail,
}) => {
  // For mobile view, we'll show a responsive card-like layout
  // For larger screens, we'll keep the table layout
  return (
    <section className="w-full">
      {/* Desktop/tablet table - hidden on small mobile */}
      <div className="hidden sm:block overflow-x-auto">
        <div className="min-w-[700px] grid grid-cols-[56px_repeat(7,1fr)] gap-0 bg-slate-50 w-full text-sm font-semibold text-slate-900 whitespace-nowrap">
          <div />

          {salesHeaders.map((header, index) => (
            <div
              key={index}
              className="p-3 sm:p-4 cursor-pointer flex justify-between items-center bg-slate-50 text-xs sm:text-sm"
            >
              {header}
            </div>
          ))}
        </div>

        <section className="w-full min-w-[700px]">
          {saleTableData.map((row, index) => (
            <section key={index}>
              <section
                className={`grid grid-cols-[56px_repeat(7,1fr)] gap-0 bg-white w-full text-xs sm:text-sm whitespace-nowrap ${
                  toggleDetailIndex !== index && 'border-b'
                }`}
              >
                <div
                  className="h-full flex items-center cursor-pointer"
                  onClick={() => onDetailClick(index)}
                >
                  <img
                    loading="lazy"
                    src={arrowDown}
                    alt="arrow-down"
                    className="w-4 aspect-square mx-auto"
                  />
                </div>

                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.customer}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.saleId}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.saleType}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.status}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.saleDate}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.saleFees}
                </div>
                <div className="flex items-center mx-2 sm:mx-4 py-3 sm:py-4 overflow-hidden text-ellipsis">
                  {row.saleAmount}
                </div>
              </section>

              {toogleDetail && toggleDetailIndex === index && (
                <DetailRow detailItems={row.detailItems} />
              )}
            </section>
          ))}
        </section>
      </div>

      {/* Mobile card view - only shown on small screens */}
      <div className="sm:hidden w-full">
        {saleTableData.map((row, index) => (
          <div 
            key={index} 
            className="mb-4 bg-white rounded-md border border-slate-200 overflow-hidden"
          >
            <div 
              className="flex justify-between items-center p-3 border-b border-slate-200 cursor-pointer"
              onClick={() => onDetailClick(index)}
            >
              <div className="font-medium">{row.saleId}</div>
              <img
                loading="lazy"
                src={arrowDown}
                alt="arrow-down"
                className="w-4 aspect-square"
              />
            </div>
            
            <div className="p-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Customer:</span>
                <span className="text-sm font-medium">{row.customer}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Sale Type:</span>
                <span className="text-sm font-medium">{row.saleType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Status:</span>
                <span className="text-sm font-medium">{row.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Sale Date:</span>
                <span className="text-sm font-medium">{row.saleDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Sale Fees:</span>
                <span className="text-sm font-medium">{row.saleFees}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-slate-500">Sale Amount:</span>
                <span className="text-sm font-medium">{row.saleAmount}</span>
              </div>
            </div>

            {toogleDetail && toggleDetailIndex === index && (
              <div className="bg-slate-50 p-3 border-t border-slate-200">
                {row.detailItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between py-1">
                    <span className="text-xs text-slate-500">{item.label}:</span>
                    <span className="text-sm">{item.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default SalesTable;
